import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Grid, Paper, Typography, Box, CircularProgress,
    useMediaQuery, IconButton, Chip, Divider
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PixIcon from "@mui/icons-material/Pix";
import api from "../../services/api";
import { getApiErrorMessage } from "../../services/apiUtils";
import { useSnackbar } from "../../contexts/SnackbarContext";

const PACKAGES = [
    { code: "EXTRA_IMG_10", quantidade: 10, valor: 8.9, label: "10 imagens" },
    { code: "EXTRA_IMG_20", quantidade: 20, valor: 12.9, label: "20 imagens" },
    { code: "EXTRA_IMG_30", quantidade: 30, valor: 14.9, label: "30 imagens" },
];

const PIX_TIMEOUT_MS = 15 * 60 * 1000; // 15 min visuais no frontend

function formatRemaining(ms) {
    const total = Math.max(0, Math.ceil(ms / 1000));
    const min = Math.floor(total / 60);
    const sec = total % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export default function ExtraImagesPackageDialog({ open, onClose, onPaid }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const pollRef = useRef(null);
    const timerRef = useRef(null);

    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pixData, setPixData] = useState(null);
    const [checking, setChecking] = useState(false);
    const [remainingMs, setRemainingMs] = useState(0);
    const [expired, setExpired] = useState(false);
    const { showSnackbar } = useSnackbar();

    const hasPix = !!pixData;
    const canCopyPix = hasPix && !expired && !!(pixData?.payload || pixData?.pix_payload);

    const stopPolling = () => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
        }
    };

    const stopTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    const resetPixState = () => {
        setPixData(null);
        setChecking(false);
        setRemainingMs(0);
        setExpired(false);
        stopPolling();
        stopTimer();
    };

    useEffect(() => {
        if (!open) {
            setSelected(null);
            setLoading(false);
            resetPixState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    useEffect(() => {
        return () => {
            stopPolling();
            stopTimer();
        };
    }, []);

    const handleClose = () => {
        if (loading) return;
        stopPolling();
        stopTimer();
        onClose?.();
    };

    const startTimer = () => {
        stopTimer();

        const endAt = Date.now() + PIX_TIMEOUT_MS;
        setExpired(false);

        const tick = () => {
            const diff = endAt - Date.now();
            setRemainingMs(Math.max(0, diff));

            if (diff <= 0) {
                setExpired(true);
                stopPolling();
                stopTimer();
            }
        };

        tick();
        timerRef.current = setInterval(tick, 1000);
    };

    const startPolling = (paymentId) => {
        stopPolling();

        pollRef.current = setInterval(async () => {
            try {
                if (expired) return;

                setChecking(true);
                const res = await api.get(`/billing/extra-images/status/${paymentId}`);

                if (res.data?.paid) {
                    stopPolling();
                    stopTimer();
                    onPaid?.(res.data);
                    return;
                }

                const paymentStatus = String(res.data?.paymentStatus || res.data?.status || "").toUpperCase();
                if (["OVERDUE", "EXPIRED", "CANCELLED"].includes(paymentStatus)) {
                    setExpired(true);
                    stopPolling();
                    stopTimer();
                }
            } catch (err) {
                console.error("Erro ao consultar status do PIX:", err);
            } finally {
                setChecking(false);
            }
        }, 4000);
    };

    const handleGeneratePix = async () => {
        if (!selected) return;

        setLoading(true);
        try {
            const res = await api.post("/billing/extra-images/pix", { packageCode: selected });

            const data = {
                ...res.data,
                paymentId: res.data?.payment_id || res.data?.paymentId,
                payload: res.data?.pix_payload || res.data?.payload,
                encodedImage: res.data?.pix_encoded_image || res.data?.encodedImage,
                quantidade: res.data?.quantity || res.data?.quantidade,
                valor: res.data?.amount || res.data?.valor,
            };

            if (data.paymentId) {
                setPixData(data);
                startTimer();
                startPolling(data.paymentId);
            }
        } catch (err) {
            showSnackbar(getApiErrorMessage(err, "Erro ao gerar PIX."), "error");
            console.error("Erro ao gerar PIX:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateNewPix = async () => {
        resetPixState();
        await handleGeneratePix();
    };

    const countdownText = useMemo(() => formatRemaining(remainingMs), [remainingMs]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
        >
            <DialogTitle sx={{ fontWeight: 900 }}>
                Pacote extra de imagens
                <IconButton onClick={handleClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {!hasPix ? (
                    <>
                        <Typography sx={{ color: "text.secondary", mb: 2 }}>
                            Escolha um pacote e gere o PIX para liberar novas imagens após a confirmação.
                        </Typography>

                        <Grid container spacing={2}>
                            {PACKAGES.map((p) => {
                                const active = selected === p.code;
                                return (
                                    <Grid item xs={12} sm={4} key={p.code}>
                                        <Paper
                                            onClick={() => setSelected(p.code)}
                                            sx={{
                                                p: 2,
                                                borderRadius: 3,
                                                cursor: "pointer",
                                                border: active
                                                    ? `2px solid ${theme.palette.primary.main}`
                                                    : `1px solid ${alpha(theme.palette.divider, 0.7)}`,
                                                background: active ? alpha(theme.palette.primary.main, 0.06) : "transparent",
                                                transition: "all 160ms ease",
                                                "&:hover": {
                                                    borderColor: theme.palette.primary.main,
                                                    transform: "translateY(-2px)"
                                                }
                                            }}
                                        >
                                            <Typography fontWeight={900}>{p.label}</Typography>

                                            <Typography sx={{ mt: 0.5, fontSize: 22, fontWeight: 900 }}>
                                                {p.valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                                            </Typography>

                                            <Typography variant="body2" sx={{ color: "text.secondary", mt: 1 }}>
                                                Saldo cumulativo, sem perder ao virar o mês.
                                            </Typography>

                                            {active && (
                                                <Chip
                                                    size="small"
                                                    icon={<CheckCircleRoundedIcon />}
                                                    label="Selecionado"
                                                    sx={{ mt: 1.5, fontWeight: 800 }}
                                                />
                                            )}
                                        </Paper>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    </>
                ) : (
                    <Box sx={{ textAlign: "center" }}>
                        <Chip
                            icon={<PixIcon />}
                            label={`PIX • ${pixData.quantidade} imagens`}
                            sx={{ mb: 2, fontWeight: 900 }}
                        />

                        <Typography sx={{ fontWeight: 900, fontSize: 22 }}>
                            {Number(pixData.valor || 0).toLocaleString("pt-BR", {
                                style: "currency",
                                currency: "BRL"
                            })}
                        </Typography>

                        <Divider sx={{ my: 2 }} />

                        {!expired ? (
                            <Chip
                                label={`Válido por ${countdownText}`}
                                color="warning"
                                variant="outlined"
                                sx={{ mb: 2, fontWeight: 800 }}
                            />
                        ) : (
                            <Chip
                                label="PIX expirado"
                                color="error"
                                sx={{ mb: 2, fontWeight: 800 }}
                            />
                        )}

                        {pixData.encodedImage && !expired ? (
                            <img
                                src={`data:image/png;base64,${pixData.encodedImage}`}
                                alt="QR Code PIX"
                                style={{
                                    width: 220,
                                    maxWidth: "100%",
                                    display: "block",
                                    margin: "0 auto 16px auto",
                                    opacity: expired ? 0.45 : 1
                                }}
                            />
                        ) : (
                            <QrCode2Icon sx={{ fontSize: 100, color: "text.secondary", mb: 2 }} />
                        )}

                        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
                            Copia e cola PIX
                        </Typography>

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                borderRadius: 2,
                                textAlign: "left",
                                wordBreak: "break-all",
                                opacity: expired ? 0.55 : 1
                            }}
                        >
                            <Typography variant="caption">
                                {pixData.payload}
                            </Typography>
                        </Paper>

                        <Box sx={{ mt: 2, display: "flex", gap: 1, justifyContent: "center", flexWrap: "wrap" }}>
                            <Button
                                variant="outlined"
                                disabled={!canCopyPix}
                                onClick={() => navigator.clipboard.writeText(pixData.payload || "")}
                                sx={{ textTransform: "none", fontWeight: 800 }}
                            >
                                Copiar código PIX
                            </Button>

                            <Button
                                variant="contained"
                                onClick={handleGenerateNewPix}
                                disabled={loading}
                                sx={{ textTransform: "none", fontWeight: 800 }}
                            >
                                {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Gerar novo PIX"}
                            </Button>
                        </Box>

                        <Typography sx={{ mt: 2, color: "text.secondary" }}>
                            {expired
                                ? "Esse PIX expirou. Gere um novo para continuar."
                                : checking
                                    ? "Verificando pagamento..."
                                    : "Aguardando pagamento..."}
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ p: 2 }}>
                <Button
                    onClick={handleClose}
                    disabled={loading}
                    sx={{ fontWeight: 900, textTransform: "none" }}
                >
                    Fechar
                </Button>

                {!hasPix && (
                    <Button
                        variant="contained"
                        onClick={handleGeneratePix}
                        disabled={!selected || loading}
                        sx={{ fontWeight: 900, textTransform: "none" }}
                    >
                        {loading ? <CircularProgress size={20} sx={{ color: "white" }} /> : "Gerar PIX"}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}